import type { Articolo, Ordine, OrdineArticolo, OrdineRiga } from "@/types";
import { ordineRigaKeys } from "@/types";
import { defineStore } from "pinia";
import { indexArray, isDivisible, leftJoinIndex, notif } from "@/utils";

import { useArticoliStore } from "@/stores/articoli";
import { useCustomerStore } from "@/stores/customers";
import { useSettingsStore } from "@/stores/settings";
import type { OrdineStored, OrdiniState } from "@/stores/ordini.types";
import { useRootStore } from "@/stores/root";
import { useLocalStorage } from "@vueuse/core";

export function validaQta(riga: OrdineArticolo, idDoc: string): boolean {
  const settingsStore = useSettingsStore();
  const totriga = riga.i_qta_pezzi +
    riga.i_pezzi_omag +
    riga.i_pezzi_pub
  ;
  if (riga.i_qta_pubb > 0 && !riga.vecswmatpubb) {
    riga.i_qta_pubb = 0;
    throw new Error("Quantità pubblicitarie non valide");
  }
  // controllo se confezioni intere
  if(settingsStore.ConsentiSfusi_get(idDoc) !== true) {
    if (!isDivisible(riga.i_qta_pezzi, riga.vecnmpez)) {
      throw new Error("Quantità non valida");
    }
  }
  if (settingsStore.ControllaGiacenzaOrdine_get(idDoc) === false) {
    return true;
  } else {
    const disponibile = riga.vecqtedi ?? 0;
    if (totriga > disponibile) {
      throw new Error(`Disponibile: ${disponibile} ( Confezioni: ${riga.vecqtediconf} )`);
    }
    return true;
  }
}

export const useOrdiniStore = defineStore({
  id: "ordiniStore",
  state: (): OrdiniState => ({
    ORDINE: useLocalStorage<{ [key: string]: OrdineStored }>(
      `isales/ORDINI/${useRootStore().VECCDAGE}`,
      {},
      {
        deep: true,
        listenToStorageChanges: true,
      }
    ),
    SHOW_ONLY_TOUCHED: false,
    MOSTRA_DATI_RISERVATI: true,
    CHIAVE_ORDINE: "",
    RIEPILOGO: undefined,
    RIEPILOGO_NOTA: useLocalStorage<{ [key: string]: string }>(
      `isales/RIEPILOGO_NOTA`,
      {},
      {
        deep: true,
        listenToStorageChanges: true,
      }
    ),
  }),
  actions: {
    async caricaPreordine(idDoc: string) {
      if (!idDoc) throw new Error("parametro tipo ordine ordine invalido");
      const customerStore = useCustomerStore();
      const settingsStore = useSettingsStore();
      const wsClient = settingsStore.wsClient;
      const CUSTOMER = customerStore.CUSTOMERS_getCurrent;
      if (!CUSTOMER) {
        console.warn("no CUSTOMER");
        return;
      }
      const response = await wsClient.GetPreordine({
        sVeccdclf: CUSTOMER?.veccdclf,
        sTipoOrdine: idDoc,
      });
      useArticoliStore().ARTICOLI_update(idDoc, response.Data);
      this.ORDINE_set_from_articoli(idDoc, response.Data);
      if (settingsStore.ControlloFido_get(idDoc)) {
        await customerStore.FIDO_update();
      }
      //TODO aggiorna budget omaggi
    },
    async creaOrdine(idDoc: string, corrispettivo = false) {
      if (!idDoc) throw new Error("parametro tipo ordine ordine invalido");
      const soapClient = useSettingsStore().wsClient;
      const customerStore = useCustomerStore();
      if (!this.RIEPILOGO) throw new Error("nessun preordine");
      this.RIEPILOGO.Testata.i_vecswcorr = corrispettivo
      const response = await soapClient.PutCreaOrdineCliente({
        sVeccdclf: customerStore.VECCDCLF,
        ordine: this.RIEPILOGO,
        sVeccddes: customerStore.VECCDDES,
        sTipoOrdine: idDoc,
      });
      if (!response.Success) {
        throw new Error(response.Message);
      }
      this.ORDINE_clean(idDoc);
      this.RIEPILOGO_unset();
      return response.Message;
    },
    SHOW_ONLY_TOUCHED_set(value: boolean) {
      this.SHOW_ONLY_TOUCHED = value;
    },
    MOSTRA_DATI_RISERVATI_set(value: boolean) {
      this.MOSTRA_DATI_RISERVATI = value;
    },
    ORDINE_set(idDoc: string, ordine: { [cdart: string]: OrdineRiga }): void {
      if (!idDoc) return;
      const key = this.ordine_key;
      this.$patch((state) => {
        if (!state.ORDINE[key]) state.ORDINE[key] = {};
        state.ORDINE[key][idDoc] = ordine;
      });
    },
    ORDINE_clean(idDoc: string) {
      this.RIEPILOGO = undefined;
      if (!idDoc) return;
      this.ORDINE_set(idDoc, {});
      this.RIEPILOGO_cancellaNota();
    },
    ORDINE_cleanAll() {
      for (const key in this.ORDINE) {
        for (const idDoc in this.ORDINE[key]) {
          this.ORDINE_set(idDoc, {});
        }
        this.ORDINE[key] = {};
      }
      this.$patch({ ORDINE: {} });
    },
    ORDINE_set_articolo(idDoc: string, articolo: OrdineArticolo | Articolo) {
      if (!idDoc) return;
      const riga = articolo as OrdineArticolo;
      riga.errors = {};
      riga.i_qta_ord = parseFloat(riga.i_qta_ord as never) || 0;
      riga.i_qta_pubb = parseFloat(riga.i_qta_pubb as never) || 0;
      riga.i_qta_omag = parseFloat(riga.i_qta_omag as never) || 0;
      riga.i_vecscri1 = parseFloat(riga.i_vecscri1 as never) || 0;
      riga.i_vecscri2 = parseFloat(riga.i_vecscri2 as never) || 0;
      riga.i_vecscri3 = parseFloat(riga.i_vecscri3 as never) || 0;
      riga.i_vecscri4 = parseFloat(riga.i_vecscri4 as never) || 0;
      riga.i_vecscri5 = parseFloat(riga.i_vecscri5 as never) || 0;
      riga.vecqtedi = parseFloat(riga.vecqtedi as never) || 0;

      try {
        validaQta(riga, idDoc);
      } catch (error) {
        if (riga.i_qta_ord > 0) {
          riga.errors["i_qta_ord"] = `${error}`;
        }
        if (riga.i_qta_omag > 0) {
          riga.errors["i_qta_omag"] = `${error}`;
        }
        if (riga.i_qta_pubb > 0) {
          riga.errors["i_qta_pubb"] = `${error}`;
        }
        riga.errors["i_qta_pezzi"] = `${error}`;
      }
      const ordine = this.ORDINE_get(idDoc);
      ordine[riga.artkey] = {
        artkey: riga.artkey,
        i_qta_ord: riga.i_qta_ord,
        i_qta_pubb: riga.i_qta_pubb,
        i_qta_omag: riga.i_qta_omag,
        i_pezzi_omag: riga.i_pezzi_omag,
        i_pezzi_pub: riga.i_pezzi_pub,
        i_vecscri1: riga.i_vecscri1,
        i_vecscri2: riga.i_vecscri2,
        i_vecscri3: riga.i_vecscri3,
        i_vecscri4: riga.i_vecscri4,
        i_vecscri5: riga.i_vecscri5,
        i_qta_pezzi: riga.i_qta_pezzi,
        errors: riga.errors,
      };
      this.ORDINE_set(idDoc, ordine);
    },
    ORDINE_set_from_articoli(idDoc: string, articoli: Articolo[]): void {
      if (!idDoc) return;
      const ordineNew = indexArray(articoli as unknown[] as OrdineRiga[], "artkey", ordineRigaKeys);
      const ordineOld = { ...this.ORDINE_get(idDoc) };
      if (Object.keys(ordineOld).length > 0) {
        for (const artKey in ordineNew) {
          if (ordineOld[artKey]) {
            ordineNew[artKey].i_qta_pubb = ordineOld[artKey].i_qta_pubb;
            ordineNew[artKey].i_qta_ord = ordineOld[artKey].i_qta_ord;
            ordineNew[artKey].i_qta_omag = ordineOld[artKey].i_qta_omag;
            ordineNew[artKey].i_vecscri1 = ordineOld[artKey].i_vecscri1;
            ordineNew[artKey].i_vecscri2 = ordineOld[artKey].i_vecscri2;
            ordineNew[artKey].i_vecscri3 = ordineOld[artKey].i_vecscri3;
            ordineNew[artKey].i_vecscri4 = ordineOld[artKey].i_vecscri4;
            ordineNew[artKey].i_vecscri5 = ordineOld[artKey].i_vecscri5;
            ordineNew[artKey].i_qta_pezzi = ordineOld[artKey].i_qta_pezzi;

            ordineNew[artKey].artkey = ordineOld[artKey].artkey; // ??
          }
        }
      }
      for (const artKey in ordineOld) {
        if (!ordineNew[artKey]) {
          ordineNew[artKey] = ordineOld[artKey];
        }
      }
      this.ORDINE_set(idDoc, ordineNew);
    },
    removeArticolo(idDoc: string, { artkey }: { artkey: string }): void {
      if (!idDoc) return;
      const key = this.ordine_key;
      if (!this.ORDINE[key]) this.ORDINE[key] = {};
      if (!this.ORDINE[key][idDoc]) this.ORDINE[key][idDoc] = {};
      const ordine = this.ORDINE_get(idDoc);
      delete ordine[artkey];
      this.ORDINE_set(idDoc, ordine);
    },
    ORDINE_addRange(idDoc: string, articoli: Articolo[]) {
      if (!idDoc) return;
      const key = this.ordine_key;
      if (!this.ORDINE[key]) this.ORDINE[key] = {};
      if (!this.ORDINE[key][idDoc]) this.ORDINE[key][idDoc] = {};
      for (const articolo of articoli) {
        if (!this.ORDINE_get(idDoc)[articolo.artkey]) {
          this.ORDINE_add(idDoc, articolo);
        }
      }
    },
    ORDINE_add(idDoc: string, articolo: Articolo) {
      this.ORDINE_set_articolo(idDoc, articolo);
    },
    RIEPILOGO_set(idDoc: string, value: Ordine) {
      this.$patch((state) => {
        state.RIEPILOGO = value;
        state.RIEPILOGO.idDoc = idDoc;
      });
    },
    RIEPILOGO_unset() {
      this.RIEPILOGO = undefined;
    },
    RIEPILOGO_cancellaNota() {
      delete this.RIEPILOGO_NOTA[this.ordine_key];
    },
    RIEPILOGO_salvaNota() {
      if (!this.RIEPILOGO) return;
      this.RIEPILOGO_NOTA[this.ordine_key] = this.RIEPILOGO?.Testata.i_vecntmem;
    },
    aggiornaRigaOrdine(idDoc: string, riga: OrdineArticolo) {
      this.ORDINE_set_articolo(idDoc, riga);
    },
    async putPreordine(idDoc: string) {
      const wsClient = useSettingsStore().wsClient;
      const sVeccdclf = useCustomerStore().VECCDCLF;
      try {
        const ordine = this.ORDINEARTICOLO_con_qta(idDoc);
        const response = await wsClient.PutPreordine({
          sVeccdclf,
          ordine,
          sTipoOrdine: idDoc,
        });
        if (!response.Success) {
          notif(response.Message, "Errore", "error");
          return;
        }
        this.RIEPILOGO_set(idDoc, response.Data);
      } catch (error) {
        notif(`Errore di comunicazione: ${error}`, "Errore", "error");
      }
    },
  },
  getters: {
    ORDINE_get(state): (idDoc: string) => OrdineStored[string] {
      return (idDoc: string) => {
        if (!idDoc) return {};
        const key = this.ordine_key;
        if (!state.ORDINE[key]) state.ORDINE[key] = {};
        if (!state.ORDINE || !state.ORDINE[key][idDoc]) return {};
        return state.ORDINE[key][idDoc];
      };
    },
    ORDINE_getRiga(): (idDoc: string, artkey: string) => OrdineRiga | undefined {
      return (idDoc, artkey) => (this.ORDINE_get(idDoc) ? this.ORDINE_get(idDoc)[artkey] : undefined);
    },
    ORDINE_get_list(state): (idDoc: string) => OrdineRiga[] {
      return (idDoc: string) => {
        if (!idDoc) return [];
        if (state.SHOW_ONLY_TOUCHED) {
          return this.ORDINE_con_qta(idDoc);
        } else {
          return this.ORDINE_get(idDoc) ? Object.values(this.ORDINE_get(idDoc)) : [];
        }
      };
    },
    ORDINE_any_with_qta(): () => boolean {
      return () => {
        for (const key in this.ORDINE) {
          for (const idDoc in this.ORDINE[key]) {
            if (this.ORDINE_con_qta(idDoc).length) return true;
          }
        }
        return false;
      }
    },
    ORDINE_con_qta(): (idDoc: string) => OrdineRiga[] {
      return (idDoc: string) => {
        if (!idDoc) return [];
        return Object.values(this.ORDINE_get(idDoc)).filter(
          (ord) => ord.i_qta_ord + ord.i_qta_omag + ord.i_qta_pubb > 0
        );
      };
    },
    ORDINEARTICOLO_con_qta(): (idDoc: string) => OrdineArticolo[] {
      return (idDoc: string) => {
        if (!idDoc) return [];
        return Object.values(this.getOrdineArticoli(idDoc)).filter(
          (ord) => ord.i_qta_ord + ord.i_qta_omag + ord.i_qta_pubb > 0
        );
      };
    },
    ORDINE_get_index(state): (idDoc: string) => OrdineStored[string] {
      return (idDoc: string) => {
        if (state.SHOW_ONLY_TOUCHED) {
          return indexArray(this.ORDINE_get_list(idDoc), "artkey");
        }
        return this.ORDINE_get(idDoc);
      };
    },
    MOSTRA_DATI_RISERVATI_get(state) {
      return state.MOSTRA_DATI_RISERVATI;
    },
    ORDINE_has_errors(): (idDoc: string) => boolean {
      return (idDoc) => {
        const ordine = this.ORDINE_con_qta(idDoc);
        if (ordine.length == 0) return true;
        for (const riga of ordine) {
          if (riga.errors) {
            if (Object.values(riga.errors).filter((e) => e).length) {
              return true;
            }
          }
        }
        return false;
      };
    },
    getOrdineArticoli(): (idDoc: string) => OrdineArticolo[] {
      return (idDoc) => {
        if (!idDoc) return [];
        const ordine = { ...this.ORDINE_get_index(idDoc) };
        const articoliStore = useArticoliStore();
        const articoli = { ...articoliStore.ARTICOLI_get(idDoc) };
        const ret = leftJoinIndex(ordine, articoli) as OrdineArticolo[];
        return ret;
      };
    },
    TOTALE_vecpzuni_qta_ord(): (idDoc: string) => number {
      return (idDoc: string) => {
        return this.getOrdineArticoli(idDoc)
          .map((art) => (art.vecpzuni ?? 0) * (art.i_qta_ord ?? 0))
          .reduce((q, s) => q + s, 0);
      }
    },
    TOTALE_vecpzuni_qta_pubb(): (idDoc: string) => number {
      return (idDoc: string) =>
        this.getOrdineArticoli(idDoc)
          .map((art) => (art.vecpzuni ?? 0) * (art.i_qta_pubb ?? 0))
          .reduce((q, s) => q + s, 0);
    },
    TOTALE_vecpzuni_qta_pubb_pezzi(): (idDoc: string) => number {
      return (idDoc: string) =>
        this.getOrdineArticoli(idDoc)
          .map((art) => (art.vecpzpez ?? 0) * (art.i_pezzi_pub ?? 0))
          .reduce((q, s) => q + s, 0);
    },
    TOTALE_vecpzuni_qta_omag_pezzi(): (idDoc: string) => number {
      return (idDoc: string) =>
        this.getOrdineArticoli(idDoc)
          .map((art) => (art.vecpzpez ?? 0) * (art.i_pezzi_omag ?? 0))
          .reduce((q, s) => q + s, 0);
    },
    TOTALE_vecpzuni_qta_omag(): (idDoc: string) => number {
      return (idDoc: string) =>
        this.getOrdineArticoli(idDoc)
          .map((art) => (art.vecpzuni ?? 0) * (art.i_qta_omag ?? 0))
          .reduce((q, s) => q + s, 0);
    },
    CUSTOMER_FIDO_ok(): (idDoc: string) => boolean {
      return (idDoc: string) => {
        const customerStore = useCustomerStore();
        if (!customerStore.FIDO_defined(idDoc)) return true;
        if (!this.TOTALE_vecpzuni_qta_ord(idDoc)) return true;
        return this.TOTALE_vecpzuni_qta_ord(idDoc) < (customerStore.FIDO ?? 0);
      };
    },
    articoloPresenteInPreordine(): (idDoc: string, articolo: { artkey: string }) => boolean {
      return (idDoc, { artkey }) => {
        return Object.keys(this.ORDINE_get(idDoc) || {}).includes(artkey);
      };
    },
    ordine_key() {
      const customerStore = useCustomerStore();
      return `${customerStore.VECCDCLF}-${customerStore.VECCDDES}`;
    },
    RIEPILOGO_confermabile() {
      const rootStore = useRootStore();
      if (rootStore.LOADING_get) {
        return false;
      }
      // nessun dato
      if (!this.RIEPILOGO?.Testata) return false;
      // lock
      if (this.RIEPILOGO.Testata.vecswocllocked) return false;
      // firma necessaria
      const settingsStore = useSettingsStore();
      if (settingsStore.RichiediFirma_get(this.RIEPILOGO.idDoc)) {
        // firma non eseguita
        if (!this.RIEPILOGO.Testata.i_sFirma) return false;
        // email non compilata
        if (!this.RIEPILOGO.Testata.i_vecdsemail) return false;
      }
      return true;
    },
    RIEPILOGO_getNota(): (idDoc: string) => string | undefined {
      const settingsStore = useSettingsStore();
      return (idDoc) => {
        const defaultValue = settingsStore.NotaDiDefault_get(idDoc);
        return this.RIEPILOGO_NOTA[this.ordine_key] || defaultValue;
      };
    },
    RIEPILOGO_corrispettivo_confermabile() {
      const rootStore = useRootStore();
      if (rootStore.LOADING_get) {
        return false;
      }
      // nessun dato
      if (!this.RIEPILOGO?.Testata) return false;
      // lock
      if (this.RIEPILOGO.Testata.vecswocplocked) return false;
      // firma necessaria
      const settingsStore = useSettingsStore();
      if (settingsStore.RichiediFirma_get(this.RIEPILOGO.idDoc)) {
        // firma non eseguita
        if (!this.RIEPILOGO.Testata.i_sFirma) return false;
        // email non compilata
        if (!this.RIEPILOGO.Testata.i_vecdsemail) return false;
      }
      return true;
    },
  },
});
